import { useState, useCallback, useRef, useEffect } from 'react';

interface PreviewItem {
  file: File;
  url: string;
}

export function useImagePreviews() {
  const [previews, setPreviews] = useState<PreviewItem[]>([]);
  const urlsRef = useRef<Set<string>>(new Set());

  const addFiles = useCallback((files: File[]) => {
    const newItems = files.map(file => {
      const url = URL.createObjectURL(file);
      urlsRef.current.add(url);
      return { file, url };
    });
    setPreviews(prev => [...prev, ...newItems]);
    return newItems;
  }, []);

  const removeAt = useCallback((index: number) => {
    setPreviews(prev => {
      const removed = prev[index];
      if (removed) {
        URL.revokeObjectURL(removed.url);
        urlsRef.current.delete(removed.url);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clearAll = useCallback(() => {
    setPreviews(prev => {
      prev.forEach(item => {
        URL.revokeObjectURL(item.url);
        urlsRef.current.delete(item.url);
      });
      return [];
    });
  }, []);

  useEffect(() => {
    const urls = urlsRef.current;
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  return { previews, addFiles, removeAt, clearAll, setPreviews };
}
